import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import { api } from "@/api/client";

interface User {
  sub: string;
  name: string;
  picture: string;
  hd: string;
  role: string;
  /** Set when the session came from local dev login rather than Google. */
  devPersona?: { key: string; label: string };
}

interface AuthContextValue {
  user: User | null;
  /** Backend User id (from GET /me) — null until loaded or if no User row exists. */
  myUserId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  myUserId: null,
  isAuthenticated: false,
  isLoading: true,
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch("/api/auth/me")
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Not authenticated");
      })
      .then((data) => setUser(data.user))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  // 404 means this identity has no User row: leave myUserId null and hide the link.
  useEffect(() => {
    if (!user) {
      setMyUserId(null);
      return;
    }
    let cancelled = false;
    api.GET("/v1/me").then(({ data }) => {
      if (!cancelled) setMyUserId(data?.userId ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const logout = () => {
    window.location.href = "/api/auth/logout";
  };

  return (
    <AuthContext.Provider
      value={{ user, myUserId, isAuthenticated: !!user, isLoading, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
